import { AttachFile, Delete, Download } from '@mui/icons-material';
import { Box, IconButton, TextField } from '@mui/material';
import { BoxProps } from '@mui/material/Box/Box';
import { TextFieldProps } from '@mui/material/TextField/TextField';
import classNames from 'classnames';
import { ChangeEvent, LegacyRef, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';

import { mergeSxProps, openInNewTab } from '~/utils/helper';

import { useSxStyles } from './useStyles';

type Props = Omit<TextFieldProps, 'onChange' | 'value'> & {
  onChange: (value: File | undefined) => void;
  value: string | null;
  accept?: string;
  attachmentUrl?: string;
  slotProps?: {
    root?: BoxProps;
    selectedFile?: BoxProps;
    downloadDivider?: BoxProps;
  };
};

export const AttachmentInput = (props: Props) => {
  const {
    onChange,
    value,
    accept,
    attachmentUrl,
    disabled,
    margin,
    slotProps,
    ...textFieldProps
  } = props;
  const { fullWidth, size, required } = textFieldProps;

  const sxStyles = useSxStyles({ margin });
  const { t } = useTranslation();

  const inputFileRef: LegacyRef<HTMLInputElement> = useRef(null);

  const handleOpenInputFilePrompt = () => {
    inputFileRef.current?.click();
  };

  const handleResetInputFile = () => {
    /* Clear hidden input file field value */
    if (!!inputFileRef.current?.value) {
      inputFileRef.current.value = '';
    }
    onChange(undefined);
  };

  const handleInputFileChange = (e: ChangeEvent<HTMLInputElement>) => {
    onChange(e.target.files ? e.target.files[0] : undefined);
  };

  const handleDownloadAttachment = () => {
    if (!!attachmentUrl) {
      openInNewTab(attachmentUrl);
    }
  };

  const iconFontSize = useMemo(() => {
    switch (size) {
      case 'small':
        return 'small';
      default:
        return undefined;
    }
  }, [size]);

  return (
    <Box
      {...slotProps?.root}
      sx={mergeSxProps(sxStyles.root, slotProps?.root?.sx)}
      className={classNames(fullWidth && 'fullWidth')}
    >
      <Box
        sx={mergeSxProps(sxStyles.selectedFile, slotProps?.selectedFile?.sx)}
        className={classNames(
          fullWidth && 'fullWidth',
          !!attachmentUrl && 'hasAttachmentUrl',
        )}
      >
        <TextField disabled value={value || ''} {...textFieldProps} />
        {!!attachmentUrl && (
          <Box
            {...slotProps?.downloadDivider}
            sx={mergeSxProps(
              sxStyles.downloadDivider,
              slotProps?.downloadDivider?.sx,
            )}
          >
            <IconButton
              onClick={() => handleDownloadAttachment()}
              title={t('laDownload')}
              size={size}
            >
              <Download />
            </IconButton>
          </Box>
        )}
      </Box>

      <IconButton
        onClick={() => handleOpenInputFilePrompt()}
        disabled={disabled}
        size={size}
      >
        <AttachFile fontSize={iconFontSize} />
      </IconButton>
      {!required && (
        <IconButton
          onClick={() => handleResetInputFile()}
          disabled={disabled}
          size={size}
        >
          <Delete fontSize={iconFontSize} />
        </IconButton>
      )}

      <Box sx={sxStyles.fileInput}>
        <input
          ref={inputFileRef}
          accept={accept}
          type="file"
          onChange={handleInputFileChange}
        />
      </Box>
    </Box>
  );
};
